import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  FaCar, 
  FaTelegram, 
  FaUsers, 
  FaRoute, 
  FaLeaf, 
  FaShieldAlt,
  FaMobile,
  FaChartLine,
  FaPlay,
  FaUserPlus
} from 'react-icons/fa';

const Home = () => {
  const features = [
    {
      icon: <FaTelegram className="h-8 w-8" />,
      title: "100% Free Platform",
      description: "Zero platform fees! Direct payment between riders and drivers. No corporate middleman taking your money."
    },
    {
      icon: <FaRoute className="h-8 w-8" />,
      title: "You Set Your Price",
      description: "Riders decide how much to pay, drivers choose which rides to accept. Complete pricing freedom for everyone."
    },
    {
      icon: <FaShieldAlt className="h-8 w-8" />,
      title: "No Restrictions",
      description: "Open platform for all. No arbitrary bans, no hidden algorithms limiting your earnings or ride options."
    },
    {
      icon: <FaLeaf className="h-8 w-8" />,
      title: "Community Service",
      description: "Built by iautenticai company as a community service. Technology that serves people, not profits."
    }
  ];

  const stats = [
    { number: "0%", label: "Platform Fees" },
    { number: "100%", label: "Driver Earnings" },
    { number: "∞", label: "Price Freedom" },
    { number: "0", label: "Restrictions" }
  ];

  return (
    <>
      <Helmet>
        <title>Boulevard - 0% Platform Fees | Beat Uber & Lyft</title>
        <meta name="description" content="The only ride-sharing platform with ZERO fees. Riders set prices, drivers keep 100%. Stop feeding corporate giants - use Boulevard's free Telegram bots." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700"></div>
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 opacity-20">
          <FaCar className="h-16 w-16 text-white animate-float" />
        </div>
        <div className="absolute top-40 right-20 opacity-20">
          <FaTelegram className="h-12 w-12 text-white animate-float" style={{animationDelay: '2s'}} />
        </div>
        <div className="absolute bottom-40 left-20 opacity-20">
          <FaUsers className="h-14 w-14 text-white animate-float" style={{animationDelay: '4s'}} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6">
              Boulevard
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-gray-200 to-gray-400">
                Zero Fees
              </span>
              Ride Sharing
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
              A community service connecting riders and drivers through Telegram bots. 
              No platform fees, fair pricing for everyone. Built by iautenticai to serve the community.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link
                to="/signup"
                className="bg-accent-500 text-white hover:bg-accent-600 font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center space-x-3 shadow-lg"
              >
                <FaUserPlus />
                <span>Sign Up</span>
              </Link>
              
              <a
                href="https://t.me/Rideatease_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white hover:bg-blue-700 font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center space-x-3 shadow-lg"
              >
                <FaTelegram />
                <span>Rider Bot </span>
              </a>
              
              <a
                href="https://t.me/Rideeasedriver_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 text-white hover:bg-green-700 font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center space-x-3 shadow-lg"
              >
                <FaTelegram />
                <span>Driver Bot </span>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-accent-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Why Choose Boulevard?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A simple community service that connects riders and drivers directly. 
              No platform fees, fair pricing, built to serve the community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card p-6 text-center"
              >
                <div className="text-primary-700 mb-4 flex justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Simple as Sending a Text Message
            </h2>
            <p className="text-xl text-gray-600">
              No apps to download, no corporate bureaucracy - just pure simplicity
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            {/* For Riders */}
            <div className="bg-blue-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-blue-800 mb-6 text-center">👥 For Riders</h3>
              <ol className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
                  <div>
                    <strong>Message @Rideatease_bot on Telegram</strong>
                    <p className="text-gray-600">Type /start and register with your name, phone, and location</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
                  <div>
                    <strong>Request a Ride with Your Price</strong>
                    <p className="text-gray-600">Type: "I need a ride from Downtown to Airport for $25"</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</span>
                  <div>
                    <strong>Get Matched with a Driver</strong>
                    <p className="text-gray-600">Drivers see your offer and accept if they like your price</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</span>
                  <div>
                    <strong>Pay the Driver Directly</strong>
                    <p className="text-gray-600">Cash, Venmo, CashApp - whatever works for both of you</p>
                  </div>
                </li>
              </ol>
            </div>

            {/* For Drivers */}
            <div className="bg-green-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-green-800 mb-6 text-center">🚗 For Drivers</h3>
              <ol className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
                  <div>
                    <strong>Message @Rideeasedriver_bot on Telegram</strong>
                    <p className="text-gray-600">Register with vehicle details and license plate</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
                  <div>
                    <strong>Set Your Availability</strong>
                    <p className="text-gray-600">Type: "I'm available at Downtown, 10 mile radius, for 3 hours"</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</span>
                  <div>
                    <strong>See Ride Requests & Choose</strong>
                    <p className="text-gray-600">Browse ride requests with prices and accept the ones you want</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</span>
                  <div>
                    <strong>Keep 100% of Payment</strong>
                    <p className="text-gray-600">No platform fees, no corporate cuts - every penny is yours</p>
                  </div>
                </li>
              </ol>
            </div>
          </div>

          {/* Example Commands */}
          <div className="bg-gray-100 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">💬 Example Commands (Natural Language)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Rider Examples:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• "I need a ride from home to work at 8am for $15"</li>
                  <li>• "Take me to the airport now, willing to pay $30"</li>
                  <li>• "Ride from Miami Beach to Downtown tomorrow 6pm, $20"</li>
                  <li>• Or just use simple commands like /riderequest</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Driver Examples:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• "I'm available in Brickell area for 4 hours, 8 mile radius"</li>
                  <li>• "Going available at South Beach, 5 miles, until 9pm"</li>
                  <li>• "Available downtown for 2 hours"</li>
                  <li>• Or use /available command</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Steps Visual */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Message Bot on Telegram",
                description: "No app downloads needed. Just message our bot on Telegram - the messaging app you already have.",
                icon: <FaTelegram className="h-12 w-12" />
              },
              {
                step: "02",
                title: "Set Price & Location",
                description: "Riders name their price, drivers set their availability. Fair and transparent for everyone.",
                icon: <FaMobile className="h-12 w-12" />
              },
              {
                step: "03",
                title: "Connect & Keep Your Money",
                description: "Get matched instantly. Pay/receive directly. No corporate middleman stealing your money.",
                icon: <FaChartLine className="h-12 w-12" />
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="text-center relative"
              >
                {index < 2 && (
                  <div className="hidden md:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-primary-600 to-primary-300 transform -translate-y-1/2"></div>
                )}
                
                <div className="bg-primary-600 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  {item.step}
                </div>
                
                <div className="text-primary-600 mb-4 flex justify-center">
                  {item.icon}
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {item.title}
                </h3>
                
                <p className="text-gray-600">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Service Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Simple Community Service
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Boulevard connects people through fair, transparent ride-sharing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* For Drivers */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-green-600 mb-6 text-center">🚗 For Drivers</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="text-green-500 text-xl">✅</span>
                  <span className="text-gray-700">Keep 100% of your earnings</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-500 text-xl">✅</span>
                  <span className="text-gray-700">Choose which rides to accept</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-500 text-xl">✅</span>
                  <span className="text-gray-700">No platform fees or commissions</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-500 text-xl">✅</span>
                  <span className="text-gray-700">Simple Telegram bot interface</span>
                </li>
              </ul>
            </div>

            {/* For Riders */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-blue-600 mb-6 text-center">👥 For Riders</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <span className="text-blue-500 text-xl">✅</span>
                  <span className="text-gray-700">Set your own fair price</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-blue-500 text-xl">✅</span>
                  <span className="text-gray-700">Connect directly with drivers</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-blue-500 text-xl">✅</span>
                  <span className="text-gray-700">No hidden fees or charges</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-blue-500 text-xl">✅</span>
                  <span className="text-gray-700">Easy messaging through Telegram</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">
              Stop Paying Corporate Middlemen!
            </h2>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Join the revolution against ride-sharing monopolies. Keep 100% of your earnings as a driver, 
              pay only what you want as a rider. No fees, no restrictions, no corporate greed.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link
                to="/signup"
                className="bg-accent-500 text-white hover:bg-accent-600 font-semibold py-4 px-8 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-3 shadow-lg"
              >
                <FaUserPlus />
                <span>Sign Up Now</span>
              </Link>
              
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <a
                  href="https://t.me/Rideatease_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 text-white hover:bg-blue-700 font-semibold py-4 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-3"
                >
                  <FaTelegram />
                  <span>Rider Bot (Request Rides)</span>
                </a>
                <a
                  href="https://t.me/Rideeasedriver_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 text-white hover:bg-green-700 font-semibold py-4 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-3"
                >
                  <FaTelegram />
                  <span>Driver Bot (Earn 100%)</span>
                </a>
              </div>
              
              <Link
                to="/contact"
                className="border-2 border-accent-400 text-accent-400 hover:bg-accent-400 hover:text-primary-900 font-semibold py-4 px-8 rounded-lg transition-all duration-200 transform hover:scale-105"
              >
                Contact Sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;
